import {
  LinearProgress,
  Box,
  Button,
  Typography,
  Chip,
  ListItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useRateLimit } from "hooks/useRateLimit";
import { useAuth } from "hooks/useAuth";
import { SettingsRow, SettingsSection } from "../SettingsSection";
import { getTrialLabelForRole } from "constants/trials";

const UsageDisplay = ({ userData = {}, subscription }) => {
  const navigate = useNavigate();
  const { hasActiveTrial, hasActiveDailyPass, subscriptionTier, organization } =
    useAuth();
  const rateLimit = useRateLimit(userData, subscription, organization);

  // Check if we're in a debug environment (staging or sandbox)
  const isDebugEnv = ["staging", "sandbox"].includes(
    process.env.REACT_APP_ENVIRONMENT,
  );

  // Determine user status for display
  const getUserStatus = () => {
    if (hasActiveDailyPass) {
      const tier = userData?.subscriptionTier || "Medium";
      return `${tier.charAt(0).toUpperCase() + tier.slice(1)} Daily Pass`;
    }
    if (hasActiveTrial) return getTrialLabelForRole(userData?.role);
    if (subscription) {
      const tier = subscriptionTier;

      if (tier) {
        return `${tier.charAt(0).toUpperCase() + tier.slice(1)} Plan`;
      }
      return "Subscribed"; // fallback for users without tier data
    }
    return "Free Plan";
  };

  const getUserStatusColor = () => {
    if (hasActiveTrial || hasActiveDailyPass || subscription) return "primary";
    return "default";
  };

  // Format token limit for display
  const formatTokenLimit = (limit) => {
    if (limit >= 1000000) {
      return `${(limit / 1000000).toFixed(1)}M`;
    }
    if (limit >= 1000) {
      return `${limit / 1000}K`;
    }
    return limit.toString();
  };

  const tokenLabel = isDebugEnv
    ? `Tokens (${rateLimit.tokensUsed?.toLocaleString()}/${rateLimit.tokenLimit?.toLocaleString()})`
    : rateLimit.isOrgProfessional
      ? `Organization token pool (${formatTokenLimit(rateLimit.tokenLimit)} tokens shared)`
      : hasActiveTrial
        ? `Trial capacity (${formatTokenLimit(rateLimit.tokenLimit)} tokens)`
        : `Monthly capacity${
            !rateLimit.isFreeTier
              ? ` (${formatTokenLimit(rateLimit.tokenLimit)} tokens)`
              : ""
          }`;

  const upgradeLabel = rateLimit.isFreeTier
    ? rateLimit.tokenPercentage >= 100
      ? "Upgrade for more monthly capacity"
      : "Upgrade for unlimited daily messages"
    : rateLimit.tokenPercentage >= 80 &&
        subscriptionTier !== "large" &&
        !hasActiveTrial
      ? `Upgrade to ${subscriptionTier === "small" ? "Medium" : "Large"} for more capacity`
      : null;

  return (
    <SettingsSection
      title="Usage"
      aside={
        <Chip
          size="small"
          label={getUserStatus()}
          color={getUserStatusColor()}
          variant="outlined"
        />
      }
    >
      <UsageMeter
        label={tokenLabel}
        percentage={rateLimit.tokenPercentage}
        color={rateLimit.getProgressColor(rateLimit.tokenPercentage)}
        note={
          rateLimit.nextReset &&
          rateLimit.tokenPercentage > rateLimit.messagePercentage
            ? `Resets ${rateLimit.nextReset}${
                rateLimit.isOrgProfessional
                  ? " (shared across all team members)"
                  : ""
              }`
            : null
        }
      />

      {/* Daily messages only limit the free tier. */}
      {rateLimit.isFreeTier && rateLimit.tokenPercentage < 100 && (
        <UsageMeter
          label={`Daily messages (${rateLimit.messageCount}/${rateLimit.messageLimit})`}
          percentage={rateLimit.messagePercentage}
          color={rateLimit.getProgressColor(rateLimit.messagePercentage)}
          note={rateLimit.nextReset ? `Resets ${rateLimit.nextReset}` : null}
        />
      )}

      {upgradeLabel && (
        <SettingsRow
          label="Need more?"
          detail={upgradeLabel}
          action={
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate("/dashboard/upgrade")}
            >
              Upgrade
            </Button>
          }
        />
      )}
    </SettingsSection>
  );
};

const UsageMeter = ({ label, percentage, color, note }) => (
  <ListItem divider disableGutters sx={{ display: "block", py: 1.5 }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
      <Typography variant="body1">{label}</Typography>
      <Typography variant="body2" color="text.secondary">
        {Math.floor(percentage)}% used
      </Typography>
    </Box>
    <LinearProgress
      variant="determinate"
      value={percentage}
      color={color}
      sx={{ height: 8, borderRadius: 4, bgcolor: "action.hover" }}
    />
    {note && (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 0.5, display: "block" }}
      >
        {note}
      </Typography>
    )}
  </ListItem>
);

export default UsageDisplay;
